// Layer 2 — Operations view (HR & line managers).
// Answers "Where is the problem?" — cohort and team-level visibility, no
// individual names.

import React from "react";
import { Box, Chip, Grid, Stack, Typography, alpha, useTheme } from "@mui/material";
import type { Theme } from "@mui/material";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import { LineChart } from "../../../../../shared/components/charts";
import {
  GlassCard,
  RosterCell,
  ScanlineBanner,
  SectionHeading,
  TierDot,
  TrafficLightCell,
  METRIC_ACCENTS,
  tierColor,
  tierLabel,
} from "./primitives";
import {
  escalations,
  metricDrivers,
  operationsAlert,
  rosterDayLabels,
  rosterForecast,
  teamStatus,
  trendSeries,
  worstCohorts,
} from "../data/mockData";

const severityColor = (sev: "LRM-1" | "LRM-2" | "LRM-3", theme: Theme) =>
  sev === "LRM-3"
    ? theme.palette.error.main
    : sev === "LRM-2"
    ? theme.palette.warning.main
    : theme.palette.info.main;

const DAYS = ["M", "T", "W", "T", "F", "S", "S"];

export const OperationsView: React.FC = () => {
  const theme = useTheme();

  return (
    <Stack spacing={3}>
      <SectionHeading
        overline="Operations view · HR & line managers"
        title="Where is the problem?"
        subtitle="Cohort and team-level visibility. No individual names."
      />

      {/* LRM traffic-light grid */}
      <GlassCard>
        <SectionHeading title="LRM status — last 7 days by team" />
        <Box sx={{ overflowX: "auto", mt: 1 }}>
          <Box sx={{ minWidth: 560 }}>
            {/* header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "200px repeat(7, 1fr) 90px 70px",
                gap: 1,
                alignItems: "center",
                mb: 1,
                px: 1,
              }}
            >
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                Team
              </Typography>
              {DAYS.map((d, i) => (
                <Typography
                  key={i}
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  fontWeight={700}
                >
                  {d}
                </Typography>
              ))}
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                fontWeight={700}
              >
                LRM rate
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="center"
                fontWeight={700}
              >
                Tier
              </Typography>
            </Box>

            {teamStatus.map((row) => (
              <Box
                key={row.team}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "200px repeat(7, 1fr) 90px 70px",
                  gap: 1,
                  alignItems: "center",
                  py: 1,
                  px: 1,
                  borderRadius: 2,
                  transition: "background 0.2s ease",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                  },
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {row.team}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.shift}
                  </Typography>
                </Box>
                {row.days.map((tier, i) => (
                  <Box key={i} sx={{ display: "flex", justifyContent: "center" }}>
                    <TrafficLightCell tier={tier} letter />
                  </Box>
                ))}
                <Typography
                  variant="body2"
                  fontWeight={800}
                  textAlign="center"
                  sx={{ color: tierColor(row.tier, theme) }}
                >
                  {row.lrmRate}%
                </Typography>
                <Stack alignItems="center">
                  <TierDot tier={row.tier} pulse={row.tier === "red"} />
                </Stack>
              </Box>
            ))}
          </Box>
        </Box>
      </GlassCard>

      {/* Predictive roster heatmap — re-roster BEFORE the shift */}
      <GlassCard glow>
        <SectionHeading
          overline="Predictive"
          title="Roster fatigue forecast — next 7 days"
          subtitle="Predicted fatigue risk (0–100) per team per shift. Re-roster the red cells before they happen."
        />
        <Box sx={{ overflowX: "auto", mt: 1 }}>
          <Box sx={{ minWidth: 620 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "180px repeat(7, 1fr)",
                gap: 1,
                mb: 1,
                px: 1,
              }}
            >
              <Box />
              {rosterDayLabels.map((d) => (
                <Typography
                  key={d}
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                  fontWeight={700}
                >
                  {d}
                </Typography>
              ))}
            </Box>
            {rosterForecast.map((row) => (
              <Box
                key={row.team}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "180px repeat(7, 1fr)",
                  gap: 1,
                  alignItems: "center",
                  mb: 1,
                  px: 1,
                }}
              >
                <Box>
                  <Typography variant="body2" fontWeight={700}>
                    {row.team}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.shift}
                  </Typography>
                </Box>
                {row.scores.map((s, i) => (
                  <RosterCell key={i} score={s} />
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </GlassCard>

      <Grid container spacing={3}>
        {/* Worst-cohort leaderboard */}
        <Grid size={{ xs: 12, md: 5 }}>
          <GlassCard>
            <SectionHeading
              title="Highest-risk cohorts"
              subtitle="Composite fatigue risk, ranked"
            />
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {worstCohorts.map((c) => {
                const color = tierColor(c.tier, theme);
                return (
                  <Box key={c.label}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="body2" fontWeight={600}>
                        {c.label}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={800}
                        sx={{ color }}
                      >
                        {c.value}
                      </Typography>
                    </Stack>
                    <Box
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        bgcolor: alpha(color, 0.15),
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${c.value}%`,
                          borderRadius: 999,
                          background: `linear-gradient(90deg, ${alpha(
                            color,
                            0.7
                          )}, ${color})`,
                          boxShadow: `0 0 8px ${alpha(color, 0.6)}`,
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          </GlassCard>
        </Grid>

        {/* Action panel — close the loop */}
        <Grid size={{ xs: 12, md: 7 }}>
          <GlassCard accent={theme.palette.error.main}>
            <SectionHeading
              overline="Action"
              title="Escalations & recommended interventions"
              subtitle="Every flag drives an action. Consecutive-day counters trigger safety escalation."
            />
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {escalations.map((e) => {
                const color = severityColor(e.severity, theme);
                return (
                  <Box
                    key={e.team}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderLeft: `3px solid ${color}`,
                      bgcolor: alpha(color, 0.06),
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                      flexWrap="wrap"
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2" fontWeight={800}>
                          {e.team}
                        </Typography>
                        <Chip
                          label={e.severity}
                          size="small"
                          sx={{
                            fontWeight: 800,
                            height: 20,
                            bgcolor: alpha(color, 0.18),
                            color,
                          }}
                        />
                      </Stack>
                      <Chip
                        icon={<EventRepeatRoundedIcon sx={{ fontSize: 14 }} />}
                        label={`${e.consecutiveDays} consecutive days`}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.75 }}>
                      <strong>{e.metric}.</strong> {e.recommendedAction}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.75 }}
                    >
                      Recommended follow-up: {e.loop}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </GlassCard>
        </Grid>
      </Grid>

      <ScanlineBanner
        tier="red"
        title="Auto-generated alert"
        body={operationsAlert}
      />

      <Grid container spacing={3}>
        {/* Trend chart */}
        <Grid size={{ xs: 12, md: 7 }}>
          <GlassCard>
            <SectionHeading
              title="Fatigue metric trends"
              subtitle="Rolling cohort averages — last 12 weeks"
            />
            <Box sx={{ mt: 1 }}>
              <LineChart
                data={trendSeries}
                xAxisKey="day"
                height={300}
                series={[
                  {
                    dataKey: "fed",
                    label: "FED",
                    color: METRIC_ACCENTS.fed,
                    area: true,
                  },
                  {
                    dataKey: "lrm",
                    label: "LRM %",
                    color: METRIC_ACCENTS.lrm,
                  },
                  {
                    dataKey: "cdi",
                    label: "CDI",
                    color: METRIC_ACCENTS.cdi,
                  },
                ]}
              />
            </Box>
          </GlassCard>
        </Grid>

        {/* What each metric drives */}
        <Grid size={{ xs: 12, md: 5 }}>
          <GlassCard>
            <SectionHeading title="What each metric drives here" />
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {metricDrivers.map((m) => {
                const accent =
                  METRIC_ACCENTS[m.code.toLowerCase() as keyof typeof METRIC_ACCENTS];
                return (
                  <Box
                    key={m.code}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      borderLeft: `3px solid ${accent}`,
                      bgcolor: alpha(accent, 0.06),
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="baseline">
                      <Typography
                        variant="caption"
                        fontWeight={800}
                        sx={{ color: accent, letterSpacing: 1 }}
                      >
                        {m.code}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {m.name}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      {m.drives}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </GlassCard>
        </Grid>
      </Grid>

      {/* legend */}
      <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap">
        {(["green", "amber", "red"] as const).map((t) => (
          <Stack key={t} direction="row" spacing={1} alignItems="center">
            <TierDot tier={t} />
            <Typography variant="caption" color="text.secondary">
              {tierLabel(t)} —{" "}
              {t === "green" ? "ready" : t === "amber" ? "monitor" : "act now"}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
